import useResetForm from 'stimulus-hooks/use-reset-form';
import ApplicationController from './application_controller';
import { cancel } from '../helpers/dom-helper';

export default class extends ApplicationController {
  static targets = ['template', 'group', 'input', 'persisted', 'option'];

  initialize() {
    this.set = new Set();
    this.permanentSelections = new Set(); // Tokens persisted from backend. Important: This shouldn't be modified.
  }

  connect() {
    useResetForm(this, { element: this.form });
  }

  persistedTargetConnected(target) {
    const { id, value } = target.dataset;
    this.set.add(id);
    this.permanentSelections.add({ id, value });
    this.selectOptionWithId(id, 'true');
  }

  optionTargetConnected(target) {
    let activated = false;

    if (this.set.has(target.id)) {
      target.setAttribute('aria-selected', 'true');
      if (activated) return;

      // Activate first connected option
      if (!this.combobox) return;
      this.combobox.setActive(target);
      activated = true;
    }
  }

  // triggered from `auto-complete:selected`
  addOrRemove(event) {
    const { id, dataset } = event.detail.relatedTarget;
    if (this.set.has(id)) {
      const token = this.groupTarget.querySelector(`[data-id="${id}"]`);
      this.removeToken(token);
    } else {
      this.set.add(id);
      this.insertToken({ id, value: dataset.autocompleteValue });
    }
  }

  // triggered from `auto-complete:reset`
  reset() {
    const tokens = this.groupTarget.querySelectorAll('[data-behavior="tokenItem"]');
    tokens.forEach((token) => this.removeToken(token));
  }

  remove(event) {
    cancel(event);
    const token = event.target.closest('[data-behavior="tokenItem"]');
    this.removeToken(token);
  }

  removeLastToken(event) {
    if (event.key !== 'Backspace') return;
    if (event.target.value.length !== 0) return;

    const lastToken = this.tokens[this.tokens.length - 1];
    this.removeToken(lastToken);
  }

  focusInput(event) {
    if (event.target !== this.element && event.target !== this.groupTarget) return;

    cancel(event);
    this.inputTarget.focus();
    this.inputTarget.dispatchEvent(new Event('focus'));
  }

  // Private

  insertToken({ id, value }) {
    const templateClone = this.templateTarget.content.cloneNode(true);
    const tokenItem = templateClone.querySelector('[data-behavior="tokenItem"]');
    const tokenName = templateClone.querySelector('[data-behavior="tokenName"]');
    const hiddenInputField = templateClone.querySelector('[data-behavior="tokenHiddenField"]');

    tokenItem.setAttribute('data-id', id);
    tokenName.innerText = value;
    hiddenInputField.value = id;

    this.groupTarget.insertBefore(templateClone, this.parentInputWrapper);
  }

  removeToken(token) {
    if (!(token instanceof HTMLElement)) return;
    const { id } = token.dataset;
    if (!id) return;

    this.set.delete(id);
    token.remove();

    // Dispatch changes on auto-complete items
    this.selectOptionWithId(id, 'false');
    this.dispatch('removed');
  }

  // Reset all tokens and make option selections based on the added tokens
  resetForm() {
    this.tokens.forEach((token) => this.removeToken(token));

    this.permanentSelections.forEach(({ id, value }) => {
      if (this.set.has(id)) return;

      this.set.add(id);
      this.insertToken({ id, value });
      this.selectOptionWithId(id, 'true');
    });
  }

  selectOptionWithId(id, value) {
    const option = this.optionTargets.find((o) => o.id === id);
    option?.setAttribute('aria-selected', value);
  }

  get listElement() {
    const listId = this.element.getAttribute('for');
    if (!listId) return undefined;

    return document.getElementById(listId);
  }

  get tokens() {
    return Array.from(this.groupTarget.querySelectorAll('[data-behavior="tokenItem"]'));
  }

  get parentInputWrapper() {
    return this.inputTarget.parentNode;
  }

  get form() {
    return this.element.closest('form');
  }

  get combobox() {
    return this.element.autocomplete?.combobox;
  }
}
